// La pantalla completa del centro de notificaciones —
// ADR-2026-08-31-centro-de-notificaciones.
//
// El popover de la campana muestra las últimas 8 y resuelve el caso frecuente
// ("¿qué pasó recién?"). Esta pantalla es el HISTORIAL: pagina hacia atrás, agrupa
// por día y deja filtrar por no leídas. Es también la única forma de ver la lista en
// mobile, donde la campana lleva derecho acá.
//
// La lista de acá NO sale del store de `useNotificaciones`: ese guarda sólo la
// primera página (lo que dibuja el popover) y se refresca solo. Mezclar la
// paginación de esta pantalla con eso haría que un refresco de fondo borrara las
// páginas que el usuario ya trajo. Acá se pagina con estado propio y del store se usa
// nada más que el contador y las acciones de marcado, que sí son compartidos.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MobilePageTitle } from '../components/MobilePageTitle';
import {
  BLOQUE_ESQUELETO,
  EsqueletoSeccion,
  claseContenido,
} from '../components/EsqueletoSeccion';
import { FilaNotificacion } from '../components/notificaciones/FilaNotificacion';
import { useNotificaciones } from '../hooks/useNotificaciones';
import {
  cargarNotificaciones,
  grupoDeFecha,
  TOPE_PAGINA,
  type Notificacion,
} from '../lib/notificaciones';

type Filtro = 'todas' | 'no-leidas';

export const Notificaciones = () => {
  const navigate = useNavigate();
  const { sinLeer, marcarTodas, marcarUna } = useNotificaciones();

  const [items, setItems] = useState<Notificacion[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [trayendoMas, setTrayendoMas] = useState(false);
  const [error, setError] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>('todas');

  useEffect(() => {
    let vigente = true;
    void (async () => {
      try {
        const pagina = await cargarNotificaciones({ limite: TOPE_PAGINA });
        if (!vigente) return;
        setItems(pagina.items);
        setCursor(pagina.cursor);
      } catch {
        if (vigente) setError(true);
      } finally {
        if (vigente) setCargando(false);
      }
    })();
    return () => { vigente = false; };
  }, []);

  const traerMas = useCallback(async () => {
    if (!cursor || trayendoMas) return;
    setTrayendoMas(true);
    try {
      const pagina = await cargarNotificaciones({ limite: TOPE_PAGINA, antes: cursor });
      // Se filtra por id contra lo que ya está: si entra una notificación nueva
      // mientras el usuario pagina, el cursor por fecha no la duplica — pero una
      // reescritura del título por el trigger de generación sí puede devolver una
      // fila que ya teníamos.
      setItems((prev) => {
        const vistos = new Set(prev.map((n) => n.id));
        return [...prev, ...pagina.items.filter((n) => !vistos.has(n.id))];
      });
      setCursor(pagina.cursor);
    } catch {
      // Silencioso: el botón queda y el usuario puede reintentar.
    } finally {
      setTrayendoMas(false);
    }
  }, [cursor, trayendoMas]);

  const abrir = (n: Notificacion) => {
    void marcarUna(n.id);
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, leida: true } : x)));
    if (n.ruta) navigate(n.ruta);
  };

  const marcarTodasAca = async () => {
    // La lista local se pinta como leída en el acto; el store se encarga del badge.
    setItems((prev) => prev.map((n) => ({ ...n, leida: true })));
    await marcarTodas();
  };

  const visibles = useMemo(
    () => (filtro === 'no-leidas' ? items.filter((n) => !n.leida) : items),
    [items, filtro],
  );

  // Agrupadas por día, como las apps de referencia (Linktree, Beli, Strava, Deel en
  // Mobbin). El popover no agrupa porque con 8 filas los encabezados ocupan más que
  // el contenido; acá, con decenas, son lo que permite orientarse.
  const grupos = useMemo(() => {
    const ahora = new Date();
    const out: { titulo: string; items: Notificacion[] }[] = [];
    for (const n of visibles) {
      const g = grupoDeFecha(n.created_at, ahora);
      const ultimo = out[out.length - 1];
      if (ultimo && ultimo.titulo === g) ultimo.items.push(n);
      else out.push({ titulo: g, items: [n] });
    }
    return out;
  }, [visibles]);

  const RAIZ = 'px-4 sm:px-8 pb-8 max-w-content mx-auto';
  const FILA_TITULO = 'hidden sm:flex items-center justify-between mb-5 h-11';

  return (
    <>
      {cargando && (
        <EsqueletoSeccion
          className={RAIZ}
          claseFilaTitulo={FILA_TITULO}
          anchoTitulo="w-[180px]"
        >
          <div className="rounded-2xl border border-black/[0.06] bg-white p-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3 px-3 py-3">
                <div className={`h-9 w-9 shrink-0 rounded-[10px] ${BLOQUE_ESQUELETO}`} />
                <div className="min-w-0 flex-1">
                  <div className={`h-[18px] w-[55%] ${BLOQUE_ESQUELETO}`} />
                  <div className={`mt-1.5 h-[17px] w-[35%] ${BLOQUE_ESQUELETO}`} />
                </div>
              </div>
            ))}
          </div>
        </EsqueletoSeccion>
      )}

      <div className={`${RAIZ} ${claseContenido(!cargando)}`}>
        <MobilePageTitle>Notificaciones</MobilePageTitle>
        <div className={FILA_TITULO}>
          <h1 className="mt-4 text-[17px] font-semibold text-brand-black/90">
            Notificaciones
          </h1>
        </div>

        {/* Barra de filtros. El contador va DENTRO de la pestaña ("No leídas (3)") y
            no como un badge aparte: es el mismo número del badge de la campana y
            repetirlo en dos formas invita a que se contradigan. */}
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            {([
              ['todas', 'Todas'],
              ['no-leidas', sinLeer > 0 ? `No leídas (${sinLeer})` : 'No leídas'],
            ] as const).map(([valor, rotulo]) => (
              <button
                key={valor}
                type="button"
                onClick={() => setFiltro(valor)}
                className={`h-[34px] rounded-xl px-3.5 text-[12.5px] font-[500] ring-1 transition-colors ${
                  filtro === valor
                    ? 'bg-[#EBEFFE] text-brand-primary ring-[rgba(22,68,235,0.22)]'
                    : 'bg-white text-black/60 ring-black/[0.08] hover:text-black/85 hover:ring-black/[0.14]'
                }`}
              >
                {rotulo}
              </button>
            ))}
          </div>
          {sinLeer > 0 && (
            <button
              type="button"
              onClick={() => void marcarTodasAca()}
              className="shrink-0 text-[12.5px] font-[500] text-brand-primary transition-opacity hover:opacity-70"
            >
              {/* En un teléfono de 390px la frase entera queda pegada a la pestaña
                  "No leídas (3)" y al borde. Es la misma acción, escrita corta. */}
              <span className="sm:hidden">Marcar todas</span>
              <span className="hidden sm:inline">Marcar todas como leídas</span>
            </button>
          )}
        </div>

        {error ? (
          <div className="rounded-2xl border border-black/[0.06] bg-white px-6 py-12 text-center">
            <p className="text-[13px] font-[600] text-black/60">
              No pudimos cargar tus notificaciones
            </p>
            <p className="mt-1 text-[12.5px] text-black/40">Probá de nuevo en un momento.</p>
          </div>
        ) : visibles.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-black/[0.06] bg-white px-6 py-16 text-center">
            <span className="material-symbols-rounded mb-2 text-[36px] text-black/20">
              notifications_off
            </span>
            <p className="text-[14px] font-[600] text-black/70">
              {filtro === 'no-leidas' ? 'No te queda nada sin leer' : 'Todo al día'}
            </p>
            <p className="mt-1 max-w-[380px] text-[12.5px] leading-[18px] text-black/40">
              {filtro === 'no-leidas'
                ? 'Ya viste todas tus notificaciones.'
                : 'Acá te vamos a avisar cuando terminen tus generaciones y cuando alguien acepte tu invitación.'}
            </p>
          </div>
        ) : (
          <>
            {grupos.map((g) => (
              <section key={g.titulo} className="mb-4">
                <p className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-black/35">
                  {g.titulo}
                </p>
                <div className="rounded-2xl border border-black/[0.06] bg-white p-1.5">
                  {g.items.map((n) => (
                    <FilaNotificacion
                      key={n.id}
                      notificacion={n}
                      onAbrir={abrir}
                      variante="pantalla"
                    />
                  ))}
                </div>
              </section>
            ))}

            {/* Botón y no scroll infinito: el historial no es una pantalla en la que
                se navegue hacia abajo sin fin, y un `IntersectionObserver` acá sólo
                agregaría pedidos que nadie pidió. */}
            {cursor && (
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  onClick={() => void traerMas()}
                  disabled={trayendoMas}
                  className="h-[34px] rounded-xl bg-white px-4 text-[12.5px] font-[500] text-black/60 ring-1 ring-black/[0.08] transition-colors hover:text-black/85 hover:ring-black/[0.14] disabled:opacity-50"
                >
                  {trayendoMas ? 'Cargando…' : 'Ver más'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Notificaciones;
